/**
 * Compact page control — first, last, the current page and its neighbours, with
 * the rest collapsed into "…".
 *
 * Lifted verbatim out of MembershipHome, which held the only copy, when the
 * course grid needed the same control. Shared rather than copied: a third
 * hand-maintained pagination would drift from the other two.
 *
 * `className` defaults to the margin both grids want, so neither of them had
 * to change; a list that sits inside a card footer passes "" and spaces it
 * with the footer's own padding instead.
 */
function Pagination({ page, totalPages, onChange, primary, className = "mt-8" }) {
  if (totalPages <= 1) return null;

  // Build a compact range: always show first & last, the current page and its
  // neighbours, and collapse the rest into "…" placeholders.
  const pages = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || (p >= page - 1 && p <= page + 1)) {
      pages.push(p);
    } else if (pages[pages.length - 1] !== "…") {
      pages.push("…");
    }
  }

  const btnBase =
    "min-w-[38px] h-[38px] px-3 inline-flex items-center justify-center rounded-lg text-sm font-semibold transition select-none";

  return (
    <nav
      className={`flex items-center justify-center gap-1.5 ${className}`}
      aria-label="Pagination"
    >
      <button
        type="button"
        onClick={() => page > 1 && onChange(page - 1)}
        disabled={page <= 1}
        className={`${btnBase} border border-gray-200 bg-surface text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed`}
        aria-label="Previous page"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      {pages.map((p, i) =>
        p === "…" ? (
          <span key={`e${i}`} className="min-w-[38px] h-[38px] inline-flex items-center justify-center text-gray-400">
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            aria-current={p === page ? "page" : undefined}
            className={`${btnBase} border ${
              p === page
                ? "text-white border-transparent"
                : "border-gray-200 bg-surface text-gray-600 hover:bg-gray-50"
            }`}
            style={p === page ? { backgroundColor: primary } : undefined}
          >
            {p}
          </button>
        )
      )}

      <button
        type="button"
        onClick={() => page < totalPages && onChange(page + 1)}
        disabled={page >= totalPages}
        className={`${btnBase} border border-gray-200 bg-surface text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed`}
        aria-label="Next page"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </nav>
  );
}

export default Pagination;
